import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUploadAlt, faChevronDown, faEye } from '@fortawesome/free-solid-svg-icons';
import AddFormModal from '../AddFormModal/AddFormModal';
import { getEmailListValidationError } from '../../utils/emailValidation';
import './EmailModal.css';

// Columns that are always required and cannot be unchecked (exact match, case-insensitive)
const MANDATORY_COLUMNS = ['name', 'patient name'];

const isMandatoryColumn = (col) =>
  MANDATORY_COLUMNS.includes(String(col).toLowerCase().trim());

const EmailModal = ({
  isOpen,
  onClose,
  onSend,
  onPreview,
  defaultTo = '',
  defaultSubject = '',
  defaultContent = '',
  availableColumns = []
}) => {
  const [emailData, setEmailData] = useState({
    to: defaultTo || '',
    cc: '',
    bcc: '',
    subject: defaultSubject || '',
    content: defaultContent || '',
    attachments: []
  });
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const prevIsOpenRef = useRef(false);

  // Reset only when the modal transitions from closed to open
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setEmailData({
        to: defaultTo || '',
        cc: '',
        bcc: '',
        subject: defaultSubject || '',
        content: defaultContent || '',
        attachments: []
      });
      setSelectedColumns(Array.isArray(availableColumns) ? [...availableColumns] : []);
      setDropdownOpen(false);
      setErrors({});
      setIsSending(false);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, defaultTo, defaultSubject, defaultContent]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [dropdownOpen]);

  const handleColumnToggle = (col) => {
    if (isMandatoryColumn(col)) return; // Cannot deselect mandatory columns
    setSelectedColumns((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]
    );
  };

  const toggleAllColumns = () => {
    if (!Array.isArray(availableColumns)) return;
    const nonMandatory = availableColumns.filter((c) => !isMandatoryColumn(c));
    const nonMandatorySelected = selectedColumns.filter((c) => !isMandatoryColumn(c));
    if (nonMandatorySelected.length === nonMandatory.length) {
      // Deselect all non-mandatory
      setSelectedColumns(availableColumns.filter(isMandatoryColumn));
    } else {
      // Select all
      setSelectedColumns([...availableColumns]);
    }
  };

  const isEmailField = (field) => field === 'to' || field === 'cc' || field === 'bcc';

  const getFieldError = (field, value) => {
    if (field === 'to') {
      return getEmailListValidationError(value, { required: true }) || '';
    }
    if (field === 'cc' || field === 'bcc') {
      return getEmailListValidationError(value, { required: false }) || '';
    }
    if (field === 'subject') {
      return String(value || '').trim() ? '' : 'Subject is required';
    }
    if (field === 'content') {
      return String(value || '').trim() ? '' : 'Content is required';
    }
    return '';
  };

  const handleChange = (field, value) => {
    setEmailData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Validate email format on blur/send only — avoid errors while the user is still typing.
    if (isEmailField(field)) {
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
      return;
    }

    setErrors((prev) => ({
      ...prev,
      [field]: getFieldError(field, value)
    }));
  };

  const handleBlur = (field) => {
    setErrors((prev) => ({
      ...prev,
      [field]: getFieldError(field, emailData[field])
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setEmailData((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...files]
      }));
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    setEmailData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const validate = () => {
    const newErrors = {};

    ['to', 'cc', 'bcc', 'subject', 'content'].forEach((field) => {
      const message = getFieldError(field, emailData[field]);
      if (message) newErrors[field] = message;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSend = async () => {
    if (!validate()) return;

    setIsSending(true);
    try {
      await onSend({ ...emailData, selectedColumns });
    } finally {
      setIsSending(false);
    }
  };

  const handleCancel = () => {
    setEmailData({
      to: '',
      cc: '',
      bcc: '',
      subject: defaultSubject || '',
      content: defaultContent || '',
      attachments: []
    });
    setSelectedColumns(Array.isArray(availableColumns) ? [...availableColumns] : []);
    setErrors({});
    onClose();
  };

  const attachmentSummary = emailData.attachments.length > 0
    ? emailData.attachments.map((file) => file.name).join(', ')
    : '';

  return (
    <AddFormModal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Email"
      className="email-modal-overlay"
      footer={(
        <div className="email-modal-footer">
          {typeof onPreview === 'function' && (
            <button
              type="button"
              className="email-btn-preview"
              onClick={() => onPreview(selectedColumns)}
              disabled={isSending}
              title="Preview report data before sending email"
            >
              <FontAwesomeIcon icon={faEye} /> Preview Report
            </button>
          )}
          <button type="button" className="email-btn-cancel" onClick={handleCancel} disabled={isSending}>
            Cancel
          </button>
          <button type="button" className="email-btn-send" onClick={handleSend} disabled={isSending}>
            {isSending ? 'Sending...' : 'Email'}
          </button>
        </div>
      )}
    >
      <div className="email-modal-body">
          <div className="email-form-row">
            <div className="email-form-field">
              <label>To <span className="required">*</span></label>
              <input
                type="text"
                className={`email-input ${errors.to ? 'error' : ''}`}
                value={emailData.to}
                onChange={(e) => handleChange('to', e.target.value)}
                onBlur={() => handleBlur('to')}
                placeholder="Enter email address"
              />
              {errors.to && <div className="error-message">{errors.to}</div>}
            </div>
            <div className="email-form-field">
              <label>CC</label>
              <input
                type="text"
                className={`email-input ${errors.cc ? 'error' : ''}`}
                value={emailData.cc}
                onChange={(e) => handleChange('cc', e.target.value)}
                onBlur={() => handleBlur('cc')}
                placeholder="Enter email address"
              />
              {errors.cc && <div className="error-message">{errors.cc}</div>}
            </div>
          </div>

          <div className="email-form-row">
            <div className="email-form-field">
              <label>BCC</label>
              <input
                type="text"
                className={`email-input ${errors.bcc ? 'error' : ''}`}
                value={emailData.bcc}
                onChange={(e) => handleChange('bcc', e.target.value)}
                onBlur={() => handleBlur('bcc')}
                placeholder="Enter email address"
              />
              {errors.bcc && <div className="error-message">{errors.bcc}</div>}
            </div>
            <div className="email-form-field">
              <label>Subject <span className="required">*</span></label>
              <input
                type="text"
                className={`email-input ${errors.subject ? 'error' : ''}`}
                value={emailData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                onBlur={() => handleBlur('subject')}
                placeholder="Enter Subject"
              />
              {errors.subject && <div className="error-message">{errors.subject}</div>}
            </div>
          </div>

          {Array.isArray(availableColumns) && availableColumns.length > 0 && (
            <div className="email-form-row">
              <div className="email-form-field email-form-field-full">
                <label>
                  Select Columns to Include in Email Attachment
                </label>
                <div className="email-col-dropdown" ref={dropdownRef}>
                  {/* Trigger */}
                  <div
                    className="email-col-trigger"
                    onClick={() => setDropdownOpen((o) => !o)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setDropdownOpen((o) => !o)}
                  >
                    <span className="email-col-trigger-label">
                      {selectedColumns.length === 0
                        ? 'Select columns...'
                        : `${selectedColumns.length} of ${availableColumns.length} selected`}
                    </span>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`email-col-chevron${dropdownOpen ? ' email-col-chevron--open' : ''}`}
                    />
                  </div>

                  {/* Dropdown Panel */}
                  {dropdownOpen && (
                    <div className="email-col-panel">
                      <div className="email-col-panel-header">
                        <button
                          type="button"
                          className="email-columns-toggle-btn"
                          onClick={toggleAllColumns}
                        >
                          {selectedColumns.filter((c) => !isMandatoryColumn(c)).length ===
                          availableColumns.filter((c) => !isMandatoryColumn(c)).length
                            ? 'Deselect All'
                            : 'Select All'}
                        </button>
                        {typeof onPreview === 'function' && (
                          <button
                            type="button"
                            className="email-columns-toggle-btn email-columns-preview-btn"
                            onClick={() => onPreview(selectedColumns)}
                            title="Preview report with current column selection"
                          >
                            <FontAwesomeIcon icon={faEye} /> Preview
                          </button>
                        )}
                      </div>
                      <div className="email-col-options">
                        {availableColumns.map((col) => {
                          const mandatory = isMandatoryColumn(col);
                          const checked = selectedColumns.includes(col);
                          return (
                            <label
                              key={col}
                              className={`email-col-option${mandatory ? ' email-col-option--locked' : ''}`}
                              title={mandatory ? 'This column is required and cannot be removed' : ''}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => handleColumnToggle(col)}
                                disabled={mandatory}
                              />
                              <span>{col}</span>
                              {mandatory && <span className="email-col-lock-badge">Required</span>}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="email-form-row">
            <div className="email-form-field email-form-field-full">
              <label>Attachments</label>
              <div className="email-attachment-container">
                <input
                  type="text"
                  className="email-input email-attachment-input"
                  readOnly
                  placeholder="No file selected"
                  value={attachmentSummary}
                />
                <button
                  type="button"
                  className="email-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FontAwesomeIcon icon={faCloudUploadAlt} /> Upload File
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
              </div>
              {emailData.attachments.length > 0 && (
                <div className="email-attachments-list">
                  {emailData.attachments.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="email-attachment-item">
                      <span className="email-attachment-name">{file.name}</span>
                      <button
                        type="button"
                        className="email-attachment-remove"
                        onClick={() => removeAttachment(index)}
                        aria-label={`Remove ${file.name}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="email-form-row">
            <div className="email-form-field email-form-field-full">
              <label>Content <span className="required">*</span></label>
              <textarea
                className={`email-textarea ${errors.content ? 'error' : ''}`}
                value={emailData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                onBlur={() => handleBlur('content')}
                placeholder="Content.."
                rows="8"
              />
              {errors.content && <div className="error-message">{errors.content}</div>}
            </div>
          </div>
      </div>
    </AddFormModal>
  );
};

export default EmailModal;
